import { useSyncExternalStore } from 'react'
import type { GenNode } from '../types/carbon/models/GenNode'
import { getNodeID } from '../utils/nodeTypes'
import { flattenTree } from '../utils/tree'

export interface RecentTable {
  path: string
  name: string
  customer: string
  job: string
  timestamp: number
  starred?: boolean
}

const STORAGE_KEY = 'platinum:recent-tables'
const MAX_RECENT = 20

// One list for the whole app. Tables, Layout and Dashboard each call the hook;
// they all read this store, so a change made on one shows on the others, and
// a change in another browser tab arrives through the storage event.
let current: RecentTable[] | null = null
const listeners = new Set<() => void>()

function loadRecent(): RecentTable[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed as RecentTable[] : []
  } catch {
    return []
  }
}

function snapshot(): RecentTable[] {
  if (current === null) current = loadRecent()
  return current
}

function publish(next: RecentTable[]) {
  current = next
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch { /* ignore quota errors */ }
  listeners.forEach(l => l())
}

function update(change: (tables: RecentTable[]) => RecentTable[]) {
  const before = snapshot()
  const next = change(before)
  if (next !== before) publish(next)
}

function onStorage(e: StorageEvent) {
  if (e.key !== STORAGE_KEY && e.key !== null) return
  current = loadRecent()
  listeners.forEach(l => l())
}

function subscribe(listener: () => void) {
  if (listeners.size === 0) window.addEventListener('storage', onStorage)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) window.removeEventListener('storage', onStorage)
  }
}

/** Forget the in-memory copy, so the next read comes from localStorage (tests). */
export function resetRecentTablesStore() {
  current = null
}

/** A table path compared without its .cbt and without case. */
const key = (path: string) => path.replace(/\\/g, '/').replace(/\.cbt$/i, '').toLowerCase()

/** The path `path` takes when `from` (a table, or a folder prefix) becomes `to`; null if it is not under `from`. */
function renamed(path: string, from: string, to: string): string | null {
  const p = path.replace(/\\/g, '/').replace(/\.cbt$/i, '')
  const f = from.replace(/\\/g, '/').replace(/\.cbt$/i, '')
  const t = to.replace(/\\/g, '/').replace(/\.cbt$/i, '')
  if (p.toLowerCase() === f.toLowerCase()) return t
  if (p.toLowerCase().startsWith(`${f.toLowerCase()}/`)) return t + p.slice(f.length)
  return null
}

/**
 * The tables this person opened recently in this job.
 * tree: the job's loaded table list (account.toc). Once it has loaded, only
 * entries still in it are shown, so a table deleted by the agent, by another
 * tab or by a colleague drops out of the list.
 */
export default function useRecentTables(customer: string, job: string, tree?: GenNode[]) {
  const tables = useSyncExternalStore(subscribe, snapshot, snapshot)
  const inJob = (t: RecentTable) => t.customer === customer && t.job === job

  const addRecent = (path: string, name: string) => {
    if (!path || !customer || !job) return
    update(prev => {
      const same = (t: RecentTable) => t.path === path && t.customer === customer && t.job === job
      const entry: RecentTable = {
        path,
        name,
        customer,
        job,
        timestamp: Date.now(),
        starred: prev.find(same)?.starred,
      }
      return [entry, ...prev.filter(t => !same(t))].slice(0, MAX_RECENT)
    })
  }

  const toggleStar = (path: string) => {
    update(prev => prev.map(t =>
      t.path === path && t.customer === customer && t.job === job
        ? { ...t, starred: !t.starred }
        : t
    ))
  }

  /** Drop deleted tables (paths with or without .cbt). */
  const removeRecent = (paths: string[]) => {
    if (paths.length === 0) return
    const gone = new Set(paths.map(key))
    update(prev => {
      const next = prev.filter(t => !(t.customer === customer && t.job === job && gone.has(key(t.path))))
      return next.length === prev.length ? prev : next
    })
  }

  /**
   * Follow a rename or a move: `from` is a table path or a folder prefix, and
   * every entry at or under it now lives at `to`. A renamed table takes its
   * new name as its title.
   */
  const renameRecent = (from: string, to: string) => {
    if (!from || !to) return
    update(prev => {
      let changed = false
      const next = prev.map(t => {
        if (t.customer !== customer || t.job !== job) return t
        const path = renamed(t.path, from, to)
        if (path === null) return t
        changed = true
        const exact = key(t.path) === key(from)
        return { ...t, path, ...(exact ? { name: path.split('/').pop() || t.name } : {}) }
      })
      return changed ? next : prev
    })
  }

  const known = tree && tree.length > 0
    ? new Set(flattenTree(tree).filter(n => n.type === 'Table').map(n => key(getNodeID(n))))
    : null

  // Tables for this customer/job, starred first, then by recency
  const recentForJob = tables
    .filter(t => inJob(t) && (!known || known.has(key(t.path))))
    .sort((a, b) => {
      if (a.starred && !b.starred) return -1
      if (!a.starred && b.starred) return 1
      return b.timestamp - a.timestamp
    })

  return {
    tables: recentForJob,
    addRecent,
    toggleStar,
    removeRecent,
    renameRecent,
  }
}
